import { getObjectionTitle, getPersonalityLabels, getPractitionerTypes } from "../../services/utils";
import { useCallback, useEffect, useMemo, useState } from "react";
import { SignedUrlImage } from "../../components/SignedUrlImage";
import { twMerge } from "tailwind-merge";
import { ResponsiveCarousel } from "./components/Carousel";
import { HCPViewSwitch } from "./components/HCPViewSwitch";
import { HCPListView } from "./components/HCPListView";
import { ObjectionLevelConfig } from "../../constant";
import { clearPractitionerFields, getOrderedHCPList } from "./praxisPlayFormUtils";

export function CharacterCard({ character, selectedCharacter, setSelectedCharacter, setForm, index, tenantProfile, group, objections, hcpTag, cardWidth = 275, cardHeight, scale = 1 }: { character: any, selectedCharacter: number, setSelectedCharacter: any, setForm: (form: any) => void, index: number, tenantProfile: any, group: string, objections: any[], hcpTag: string, cardWidth?: number, cardHeight?: number, scale?: number }) {
    const personalities = getPersonalityLabels(character?.personalityType, tenantProfile, group, hcpTag);
    const practitionerTypes = getPractitionerTypes(tenantProfile?.practitioner_types || {}, group) || [];
    const practitionerType = practitionerTypes.find((type) => type.key === character.practitionerType || type.type === character.practitionerType);
    const objectionLevelConfig = ObjectionLevelConfig[character.objectionLevel as keyof typeof ObjectionLevelConfig] ?? {
        backgroundColor: "#F3F4F6",
        color: "#374151",
    };
    const imageSize = Math.round(107 * scale);
    const badgeWidth = Math.round(150 * scale);
    const badgeHeight = Math.round(30 * scale);
    const badgeTop = Math.round(14 * scale);
    const personalityBadgeHeight = Math.round(23 * scale);
    // Inset the personality pill proportionally to the card width. At the 275px
    // reference width this matches the original 48px (mx-12) inset; on narrower
    // cards (e.g. the 150px mobile width) it shrinks so the label isn't clipped.
    const personalityBadgeMarginX = Math.round((48 / 275) * cardWidth);
    const objectionBadgeHeight = Math.round(23 * scale);
    const objectionBadgeWidth = Math.round(80 * scale);
    return (
        <div
            style={{
                width: cardWidth,
                minWidth: cardWidth,
                maxWidth: cardWidth,
                height: cardHeight,
            }}
            className={twMerge("relative flex flex-col min-h-0 rounded-[32px]", selectedCharacter === index ? "bg-[linear-gradient(180deg,var(--PraxisDarkRed)_0%,var(--loginBgGradient2)_52%,var(--loginBgGradient3)_100%)]" : "bg-gray-402")}
        >
            {character.isLastHCP && (
                <div
                    style={{
                        top: -badgeTop,
                        width: badgeWidth,
                        height: badgeHeight,
                        fontSize: Math.round(12 * scale),
                    }}
                    className="absolute flex items-center justify-center left-1/2 -translate-x-1/2 bg-[linear-gradient(180deg,var(--PraxisDarkRed)_-38.33%,var(--primary5)_183.33%)] text-white px-2 py-1 rounded-full"
                >
                    Last Selected HCP
                </div>
            )}
            <div
                data-tutorial-hcp-card-selected={selectedCharacter === index ? true : undefined}
                className={`flex flex-col items-center min-h-0 h-[calc(100%-5px)] max-h-[calc(100%-5px)] justify-evenly gap-[clamp(0.25rem,1.2vh,0.5rem)] px-3 m-[2.5px] rounded-[30px] bg-background shadow-[0px_4.54px_10.896px_0px_rgba(0,0,0,0.20)] overflow-y-auto ${selectedCharacter === index
                    ? "!bg-background2"
                    : ""
                    }`}
                style={{
                    paddingTop: Math.round((selectedCharacter === index ? 40 : 28) * scale),
                    paddingBottom: Math.round((selectedCharacter === index ? 40 : 28) * scale),
                }}
                role="button"
                onClick={() => {
                    setSelectedCharacter(index);
                    const { isLastHCP, ...characterData } = character;
                    setForm((prev) => ({
                        ...prev,
                        ...characterData,
                    }));
                }}
            >
                <div className="shrink-0">
                    <SignedUrlImage
                        s3Key={character.practitionerImage}
                        bucket={tenantProfile?.assets_bucket}
                        alt={character.practitionerType}
                        style={{ width: imageSize, height: imageSize }}
                        className="mx-auto object-contain"
                    />
                </div>
                <div
                    className="shrink-0 text-center font-medium leading-tight text-text2"
                    style={{ fontSize: Math.round(20 * scale) }}
                >
                    {practitionerType?.type || practitionerType?.key}
                </div>
                <div
                    className="flex shrink-0 flex-col items-center justify-center gap-1 text-text2"
                    style={{ fontSize: Math.round(13 * scale) }}
                >
                    <div style={{ fontSize: Math.round(15 * scale) }}>Personality type:</div>
                    <div
                        style={{
                            height: personalityBadgeHeight,
                            fontSize: Math.round(10 * scale),
                            marginLeft: personalityBadgeMarginX,
                            marginRight: personalityBadgeMarginX,
                        }}
                        className="flex items-center justify-center bg-gray-100 rounded-md px-2 font-semibold text-gray-800"
                    >
                        {personalities}
                    </div>
                </div>
                <div
                    className="flex shrink-0 flex-col items-center justify-center gap-1 text-text2"
                    style={{ fontSize: Math.round(13 * scale) }}
                >
                    <div style={{ fontSize: Math.round(15 * scale) }}>{tenantProfile?.objections_title || "Objection Level"}:</div>
                    <div
                        className="flex items-center justify-center rounded-md font-semibold"
                        style={{
                            backgroundColor: objectionLevelConfig.backgroundColor,
                            color: objectionLevelConfig.color,
                            width: objectionBadgeWidth,
                            height: objectionBadgeHeight,
                            fontSize: Math.round(10 * scale),
                        }}
                    >
                        {getObjectionTitle(character.objectionLevel, objections)}
                    </div>
                </div>
            </div>
        </div>
    )
}

export function PraxisPlayHCPScreen({
    characterList,
    selectedCharacter,
    setSelectedCharacter,
    form,
    setForm,
    tenantProfile,
    userInfo,
    groupInfo,
    hcpTag,
    setHcpTag,
}: {
    characterList: any[];
    selectedCharacter: number;
    setSelectedCharacter: (character: number) => void;
    form: any;
    setForm: (form: any) => void;
    tenantProfile: any;
    userInfo: any;
    groupInfo: any;
    hcpTag: string;
    setHcpTag: (tag: string) => void;
}) {
    const objections = tenantProfile?.objections || [];
    const [view, setView] = useState<"list" | "grid">("grid");

    const filteredList = useMemo(
        () => getOrderedHCPList(characterList, hcpTag),
        [characterList, hcpTag],
    );

    const handleCarouselChange = useCallback((index: number) => {
        setSelectedCharacter(index);
        const character = filteredList[index];
        if (character) {
            const { isLastHCP, ...characterData } = character;
            setForm((prev: any) => ({ ...prev, ...characterData }));
        }
    }, [filteredList, setSelectedCharacter, setForm]);

    const carouselSelectedCharacter = useMemo(() => {
        const n = filteredList.length;
        if (n === 0) return selectedCharacter;
        return Math.min(Math.max(0, selectedCharacter), n - 1);
    }, [filteredList.length, selectedCharacter]);

    const slideContext = useMemo(() => ({
        hcpTag,
        tenantProfile,
        group: userInfo?.group,
        objections,
        selectedCharacter: carouselSelectedCharacter,
        setForm,
        form,
    }), [hcpTag, tenantProfile, userInfo?.group, objections, carouselSelectedCharacter, setForm, form]);

    useEffect(() => {
        const list = getOrderedHCPList(characterList, hcpTag);
        if (list.length === 0) {
            if (characterList.length > 0) {
                setSelectedCharacter(0);
            }
            setForm((prev) => clearPractitionerFields(prev));
            return;
        }
        const initialIndex = list.length >= 2 ? 1 : 0;
        setSelectedCharacter(initialIndex);
        const selected = list[initialIndex] ?? list[0];
        if (selected) {
            const { isLastHCP, ...characterData } = selected;
            setForm((prev) => ({ ...prev, ...characterData }));
        }
    }, [hcpTag, characterList]);

    return (
        <div className="w-full flex-1 flex-col justify-start items-center tall2:gap-5 gap-4 flex">
            <div className="flex flex-col gap-2 items-start justify-start w-full">
                <div className="flex items-center justify-between w-full">
                    <div className="self-stretch text-left text-text-primary text-[15px] md:text-base font-semibold">
                        Select or customize an AI HCP to practice with <br />
                    </div>
                    <HCPViewSwitch view={view} setView={setView} />
                </div>
                {groupInfo?.hcpTags?.length > 0 && <div className="flex gap-[1px] rounded-full p-1 bg-gray-100">
                    {groupInfo?.hcpTags?.map((tag: string) => (
                        <button onClick={() => setHcpTag(tag)} key={tag} className={twMerge("text-text-gray py-2 px-8 rounded-full text-[13px] font-medium", hcpTag === tag ? "text-text-primary bg-gray2" : "")}>
                            {tag}
                        </button>
                    ))}
                </div>}
            </div>
            <div className={twMerge("flex-1 flex flex-col items-center relative tall2:gap-3 gap-7 w-full min-h-0", view === "grid" ? "justify-center" : "justify-start")}>
                <div className="flex justify-center items-center gap-6 w-full">
                    {view === "grid" && <ResponsiveCarousel
                        data={filteredList}
                        onChange={handleCarouselChange}
                        isLoading={false}
                        slideContext={slideContext}
                    />}
                    {view === "list" && <HCPListView data={filteredList} onChange={handleCarouselChange} isLoading={false} {...slideContext} />}
                </div>
            </div>
        </div>
    );
}
